// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Axios Imports
import {
    getWebAdvertisementApi,
    getWebAdvertisementByIdApi,
    addOrUpdateWebAdvertisementApi,
    getPendingAdsApi,
    approveAdApi,
    rejectAdApi,
    updateAdMetadataApi,
    getVendorSlotsForManagementApi,
    getAdminSlotsForManagementApi,
    uploadAdToSlotApi
} from "../../../../services/Web/webAdvertisement.service"

import { toastError, toastSuccess } from "../../../../utility/toastutill"

// ** Async Thunks
export const fetchAdvertisements = createAsyncThunk(
    "adSection/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getWebAdvertisementApi()
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to fetch web advertisement data"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const fetchAdvertisementById = createAsyncThunk(
    "adSection/fetchById",
    async (id, { rejectWithValue }) => {
        try {
            const response = await getWebAdvertisementByIdApi(id)
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to fetch web advertisement data"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const createOrUpdateAdvertisement = createAsyncThunk(
    "adSection/createOrUpdate",
    async (formData, { rejectWithValue }) => {
        try {
            const response = await addOrUpdateWebAdvertisementApi(formData)
            toastSuccess("Web advertisement data saved successfully")
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to save web advertisement data"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const fetchPendingAds = createAsyncThunk(
    "adSection/fetchPending",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getPendingAdsApi()
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to fetch pending advertisements"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const approveAdvertisement = createAsyncThunk(
    "adSection/approve",
    async ({ sectionId, adImage }, { rejectWithValue }) => {
        try {
            const response = await approveAdApi(sectionId, adImage)
            toastSuccess("Advertisement approved successfully")
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to approve advertisement"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const rejectAdvertisement = createAsyncThunk(
    "adSection/reject",
    async ({ sectionId, adImage }, { rejectWithValue }) => {
        try {
            const response = await rejectAdApi(sectionId, adImage)
            toastSuccess("Advertisement rejected successfully")
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to reject advertisement"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

// Update ad metadata (order, keywords) - admin only
export const updateAdMetadata = createAsyncThunk(
    "adSection/updateAdMetadata",
    async (updates, { rejectWithValue }) => {
        try {
            const response = await updateAdMetadataApi(updates)
            toastSuccess("Ads updated successfully")
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to update ads"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const fetchVendorSlots = createAsyncThunk(
    "adSection/fetchVendorSlots",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getVendorSlotsForManagementApi()
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to fetch your ad slots"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const uploadAdToSlot = createAsyncThunk(
    "adSection/uploadAdToSlot",
    async ({ slotId, formData }, { rejectWithValue }) => {
        try {
            const response = await uploadAdToSlotApi(slotId, formData)
            toastSuccess("Ad content uploaded successfully")
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to upload ad content"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

export const fetchAdminSlots = createAsyncThunk(
    "adSection/fetchAdminSlots",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getAdminSlotsForManagementApi()
            return response.data
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || "Failed to fetch admin slots"
            toastError(errorMessage)
            return rejectWithValue({
                message: errorMessage,
                status: error.response?.status
            })
        }
    }
)

// ** Initial State
const initialState = {
    data: null,
    advertisements: null,
    vendorSlots: [],
    pendingAds: [],
    loading: false,
    pendingLoading: false,
    error: null,
    total: 0,
    lastUpdated: null
}

// ** Slice
export const AdSectionSlice = createSlice({
    name: "adSection",
    initialState,
    reducers: {
        clearSelectedAd: (state) => {
            state.advertisements = null
        },
        clearError: (state) => {
            state.error = null
        },
        resetAdState: (state) => {
            state.data = null
            state.advertisements = null
            state.loading = false
            state.error = null
            state.total = 0
            state.lastUpdated = null
        },
        updateFormData: (state, action) => {
            const updatedData = action.payload

            if (state.advertisements) {
                state.advertisements = {
                    ...state.advertisements,
                    ...updatedData
                }
            }

            if (state.data) {
                state.data = {
                    ...state.data,
                    ...updatedData
                }
            }

            state.lastUpdated = new Date().toISOString()
        },
        // Add a specific update for buttons
        updateButtonsData: (state, action) => {
            const { button1, button2 } = action.payload

            if (state.advertisements) {
                if (button1) state.advertisements.button1 = button1
                if (button2) state.advertisements.button2 = button2
            }

            if (state.data) {
                if (button1) state.data.button1 = button1
                if (button2) state.data.button2 = button2
            }

            state.lastUpdated = new Date().toISOString()
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all web home data
            .addCase(fetchAdvertisements.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchAdvertisements.fulfilled, (state, action) => {
                state.loading = false
                const responseData = action.payload.data || action.payload

                state.data = responseData
                state.advertisements = responseData
                state.total = 1
                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(fetchAdvertisements.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
                state.lastUpdated = new Date().toISOString()
            })
            // Fetch web home by ID
            .addCase(fetchAdvertisementById.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchAdvertisementById.fulfilled, (state, action) => {
                state.loading = false
                const responseData = action.payload.data || action.payload
                state.advertisements = responseData

                if (state.data && state.data._id === responseData._id) {
                    state.data = responseData
                }
                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(fetchAdvertisementById.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
                state.lastUpdated = new Date().toISOString()
            })
            // Create or update web home
            .addCase(createOrUpdateAdvertisement.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(createOrUpdateAdvertisement.fulfilled, (state, action) => {
                state.loading = false
                const updatedData = action.payload.data || action.payload

                state.data = updatedData
                state.advertisements = updatedData
                state.total = 1
                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(createOrUpdateAdvertisement.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
                state.lastUpdated = new Date().toISOString()
            })
            // Fetch pending ads
            .addCase(fetchPendingAds.pending, (state) => {
                state.pendingLoading = true
                state.error = null
            })
            .addCase(fetchPendingAds.fulfilled, (state, action) => {
                state.pendingLoading = false
                state.pendingAds = action.payload.data || []
                state.error = null
            })
            .addCase(fetchPendingAds.rejected, (state, action) => {
                state.pendingLoading = false
                state.error = action.payload
            })
            // Admin Slots
            .addCase(fetchAdminSlots.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchAdminSlots.fulfilled, (state, action) => {
                state.loading = false
                state.advertisements = action.payload.data || []
            })
            .addCase(fetchAdminSlots.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Approve advertisement
            .addCase(approveAdvertisement.pending, (state) => {
                state.loading = true
            })
            .addCase(approveAdvertisement.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(approveAdvertisement.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Reject advertisement
            .addCase(rejectAdvertisement.pending, (state) => {
                state.loading = true
            })
            .addCase(rejectAdvertisement.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(rejectAdvertisement.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Vendor Slots
            .addCase(fetchVendorSlots.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchVendorSlots.fulfilled, (state, action) => {
                state.loading = false
                const sections = action.payload.sections || {}
                const flattenedSlots = []

                Object.entries(sections).forEach(([sectionName, slots]) => {
                    slots.forEach(slot => {
                        flattenedSlots.push({
                            ...slot,
                            section: sectionName,
                            _id: slot.adSlotId // Use adSlotId as _id for consistency
                        })
                    })
                })

                state.vendorSlots = flattenedSlots
            })
            .addCase(fetchVendorSlots.rejected, (state) => {
                state.loading = false
            })
            .addCase(uploadAdToSlot.pending, (state) => {
                state.loading = true
            })
            .addCase(uploadAdToSlot.fulfilled, (state, action) => {
                state.loading = false
                // The API might return the updated slot object
                const updatedSlot = action.payload.data || action.payload
                if (state.vendorSlots) {
                    const index = state.vendorSlots.findIndex(s => s._id === (updatedSlot.adSlotId || updatedSlot._id))
                    if (index !== -1) {
                        // Ensure it keeps the section property
                        const section = state.vendorSlots[index].section
                        state.vendorSlots[index] = { ...updatedSlot, section, _id: updatedSlot.adSlotId || updatedSlot._id }
                    }
                }
            })
            .addCase(uploadAdToSlot.rejected, (state) => {
                state.loading = false
            })
    }
})

// ** Actions
export const {
    clearSelectedAd,
    clearError,
    resetAdState,
    updateFormData,
    updateButtonsData,
    setLoading
} = AdSectionSlice.actions

// ** Selectors
export const selectWebHome = (state) => state.adSection.data
export const selectSelectedWebHome = (state) => state.adSection.advertisements
export const selectWebHomeLoading = (state) => state.adSection.loading
export const selectWebHomeError = (state) => state.adSection.error
export const selectWebHomeLastUpdated = (state) => state.adSection.lastUpdated

// ** Reducer
export default AdSectionSlice.reducer
